import type { BuyerImportBatchStatus } from "@/api/buyerSmartImport";
import type { StatusTone } from "@/design-system/components/ui";

/**
 * How a batch's status paints, in one place.
 *
 * <p>The progress screen and the history table both badge the same five
 * statuses, and a reader looking at one after the other is entitled to read the
 * same colour as the same condition. Two maps would eventually disagree about
 * `Undone`, which is the one status whose right answer is not obvious — it is
 * neither a success nor a failure, so it is MUTED rather than either: an undo
 * is somebody deliberately putting the book back, and colouring it red would
 * report a routine correction as an incident.</p>
 *
 * <p>Typed as an exhaustive `Record` so a status added to the union without an
 * entry here is a typecheck error rather than a silent fall-through to grey.</p>
 */
export const BATCH_STATUS_TONES: Record<BuyerImportBatchStatus, StatusTone> = {
  Queued: "neutral",
  Running: "progress",
  Completed: "ok",
  Failed: "danger",
  Undone: "muted",
};

export function batchStatusTone(status: BuyerImportBatchStatus): StatusTone {
  return BATCH_STATUS_TONES[status] ?? "neutral";
}

/*
  Deliberately NOT a home for "is this status terminal?" —
  `isTerminalBatchStatus` lives with the poll in `hooks/buyers/useSmartImport`,
  which is the only thing that acts on the answer. This module is view
  vocabulary and nothing else.
*/
